import { predPnl, type PredictionLeg } from "../lib/composePayoff";
import type { SelectedPerp, SelectedPred } from "../types";
import {
  Field,
  NumberInput,
  ReadoutRow,
  SegmentToggle,
  readoutSectionStyle,
  sectionStyle,
} from "./sim-form-primitives";

const PRED_COLOR = "#60a5fa";

function formatUsd(n: number): string {
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function signedUsd(n: number): string {
  if (!isFinite(n)) return "—";
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
  return neg ? `−${abs}` : `+${abs}`;
}

export function PredictionSimulateForm({
  market,
  perp,
  perpSymbol,
  leg,
  onChange,
}: {
  market: SelectedPred;
  /** The selected perp, used to evaluate P&L at current mark. */
  perp: SelectedPerp | null;
  perpSymbol: string | undefined;
  leg: PredictionLeg;
  onChange: (next: PredictionLeg) => void;
}) {
  const patch = (p: Partial<PredictionLeg>) => onChange({ ...leg, ...p });

  const costBasis = (leg.shares * leg.entryCents) / 100;
  const maxPayout = leg.shares;
  const maxLoss = costBasis;
  const impliedProb = leg.entryCents / 100;
  const atMark = perp ? predPnl(leg, perp.markPx) : null;

  const yesMarketCents = market.yesPrice !== undefined ? market.yesPrice * 100 : undefined;
  const noMarketCents = market.noPrice !== undefined ? market.noPrice * 100 : undefined;

  const applyMarketMid = () => {
    const mid = leg.outcome === "yes" ? yesMarketCents : noMarketCents;
    if (mid !== undefined) patch({ entryCents: parseFloat(mid.toFixed(1)) });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={sectionStyle}>
        <Field label="OUTCOME">
          <SegmentToggle
            value={leg.outcome}
            onChange={(v) => patch({ outcome: v })}
            options={[
              { value: "yes", label: "YES", color: "var(--up)" },
              { value: "no", label: "NO", color: "var(--dn)" },
            ]}
          />
        </Field>
        <Field label="SHARES">
          <NumberInput
            value={leg.shares}
            min={0}
            step={1}
            onChange={(n) => patch({ shares: Math.max(0, n) })}
          />
        </Field>
        <Field label="ENTRY">
          <NumberInput
            value={leg.entryCents}
            min={0}
            max={100}
            step={0.1}
            suffix="¢"
            onChange={(n) =>
              patch({ entryCents: Math.max(0, Math.min(100, n)) })
            }
          />
        </Field>
        <div
          style={{
            display: "flex",
            gap: 4,
            fontFamily: "var(--f-mono)",
            fontSize: 9,
            letterSpacing: "0.08em",
          }}
        >
          <button
            type="button"
            onClick={applyMarketMid}
            style={{
              flex: 1,
              background: "transparent",
              color: "var(--fg-dim)",
              border: "1px solid var(--line-2)",
              padding: "4px 0",
              cursor: "pointer",
              borderRadius: 2,
            }}
          >
            USE MID{" "}
            {leg.outcome === "yes"
              ? yesMarketCents !== undefined
                ? `${yesMarketCents.toFixed(1)}¢`
                : "—"
              : noMarketCents !== undefined
                ? `${noMarketCents.toFixed(1)}¢`
                : "—"}
          </button>
          <button
            type="button"
            onClick={() =>
              patch({
                entryCents: parseFloat(
                  Math.min(100, leg.entryCents + 1).toFixed(1),
                ),
              })
            }
            style={{
              width: 44,
              background: "transparent",
              color: "var(--fg-dim)",
              border: "1px solid var(--line-2)",
              padding: "4px 0",
              cursor: "pointer",
              borderRadius: 2,
            }}
          >
            +1¢
          </button>
          <button
            type="button"
            onClick={() =>
              patch({
                entryCents: parseFloat(
                  Math.max(0, leg.entryCents - 1).toFixed(1),
                ),
              })
            }
            style={{
              width: 44,
              background: "transparent",
              color: "var(--fg-dim)",
              border: "1px solid var(--line-2)",
              padding: "4px 0",
              cursor: "pointer",
              borderRadius: 2,
            }}
          >
            −1¢
          </button>
        </div>
      </div>

      <div style={sectionStyle}>
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 9,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--fg-dim)",
          }}
        >
          COUPLING · YES RESOLVES IF {perpSymbol ?? "UNDERLYING"} IS
        </div>
        <Field label="DIRECTION">
          <SegmentToggle
            value={leg.direction}
            onChange={(v) => patch({ direction: v })}
            options={[
              { value: "above", label: "ABOVE", color: "var(--up)" },
              { value: "below", label: "BELOW", color: "var(--dn)" },
            ]}
          />
        </Field>
        <Field label="THRESHOLD">
          <NumberInput
            value={leg.threshold}
            min={0}
            step={0.01}
            suffix="USD"
            onChange={(n) => patch({ threshold: Math.max(0, n) })}
          />
        </Field>
      </div>

      <div style={readoutSectionStyle}>
        <ReadoutRow
          label="IMPLIED PROB"
          value={`${(impliedProb * 100).toFixed(1)}%`}
        />
        <ReadoutRow label="COST BASIS" value={formatUsd(costBasis)} />
        <ReadoutRow label="MAX PAYOUT" value={formatUsd(maxPayout)} />
        <ReadoutRow
          label="MAX LOSS"
          value={formatUsd(-maxLoss)}
          color="var(--dn)"
        />
      </div>

      <div
        style={{
          padding: "12px 14px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--bg-0)",
          borderTop: "1px solid var(--line-2)",
        }}
      >
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 9,
            letterSpacing: "0.16em",
            color: "var(--fg-meta)",
            textTransform: "uppercase",
          }}
        >
          AT MARK
        </span>
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 16,
            fontVariantNumeric: "tabular-nums",
            fontWeight: 500,
            color:
              atMark === null
                ? "var(--fg-dim)"
                : atMark >= 0
                  ? "var(--up)"
                  : "var(--dn)",
          }}
        >
          {atMark === null ? "—" : signedUsd(atMark)}
        </span>
      </div>
    </div>
  );
}
